import { Grid, Link, Typography } from '@material-ui/core';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandIcon from '@mui/icons-material/AddBoxRounded';
import CollapseIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';

import { useStyles } from '../SystemSettings.styles';
import { useTranslation } from 'react-i18next';
import { SelectableTable } from '../../SelectableTable/SelectableTable.component';
import { SelectableTableColumn } from '../../SelectableTable/SelectableTable.types';
import { useEffect, useState } from 'react';
import { LabelsListItem, LabelsListTableItem, ViewWithLabels, ViewWithLabelsDto } from './Label.types';
import { EditLabel } from './EditLabel.component';
import { getLabels, updateLabel } from '../../../services/admin/settings/systemSettings.service';
import { useLayoutContext } from '../../../Context/Layout.context';
import i18next from 'i18next';
import TooltipOneComponent from '../../TooltipOne/TooltipOne.component';

interface labelManagerProps {
  onClose: () => void;
  onChange: () => void;
}

export const LabelManager: React.FC<labelManagerProps> = ({ onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [selectedLabel, setSelectedLabel] = useState<LabelsListItem>();
  const [selectedView, setSelectedView] = useState<ViewWithLabels>();
  const [isEditLabelModalOpen, setIsEditLabelModalOpen] = useState<boolean>(false);
  const [allLabels, setAllLabels] = useState<ViewWithLabelsDto[]>([]);
  const [tableDataMap, setTableDataMap] = useState<{ [key: string]: LabelsListTableItem[] }>({});
  const [expandedViews, setExpandedViews] = useState<string[]>([]);

  const { showLoader, openAlert } = useLayoutContext();

  const getData = () => {
    getLabels().then(
      (response) => {
        setAllLabels(response);
      },
      (error) => console.log(error),
    );
  };

  useEffect(() => {
    getData();
  }, []);

  const getViewId = (name: string) => {
    return allLabels.find((e) => e.name === name)?.id ?? '';
  };

  const viewsWithLabels = [
    {
      id: getViewId('users'),
      name: "Widok 'Użytkownicy'",
      labels: [
        {
          type: 'Nagłówek widoku',
          name: 'header.title',
          value: t('users:header.title'),
        },
        {
          type: 'Przycisk',
          name: 'header.addUserButton',
          value: t('users:header.addUserButton'),
        },
        {
          type: 'Kolumna',
          name: 'table.firstName',
          value: t('users:table.firstName'),
        },
        {
          type: 'Kolumna',
          name: 'table.lastName',
          value: t('users:table.lastName'),
        },
        {
          type: 'Kolumna',
          name: 'table.email',
          value: t('users:table.email'),
        },
        {
          type: 'Kolumna',
          name: 'table.pesel',
          value: t('users:table.pesel'),
        },
        {
          type: 'Kolumna',
          name: 'table.accountTypeName',
          value: t('users:table.accountTypeName'),
        },
        {
          type: 'Kolumna',
          name: 'table.examCommittee',
          value: t('users:table.examCommittee'),
        },
        {
          type: 'Kolumna',
          name: 'table.phone',
          value: t('users:table.phone'),
        },
        {
          type: 'Kolumna',
          name: 'table.accountStatus',
          value: t('users:table.accountStatus'),
        },
      ],
    },
    {
      id: getViewId('roles'),
      name: "Widok 'Role i uprawnienia'",
      labels: [
        {
          type: 'Nagłówek widoku',
          name: 'header.title',
          value: t('roles:header.title'),
        },
        {
          type: 'Nagłówek widoku',
          name: 'header.rolesSum',
          value: t('roles:header.rolesSum'),
        },
        {
          type: 'Nagłówek widoku',
          name: 'header.permissionsSum',
          value: t('roles:header.permissionsSum'),
        },
        {
          type: 'Przycisk',
          name: 'header.addRoleButton',
          value: t('roles:header.addRoleButton'),
        },
        {
          type: 'Kolumna',
          name: 'table.name',
          value: t('roles:table.name'),
        },
        {
          type: 'Kolumna',
          name: 'table.roleDescription',
          value: t('roles:table.roleDescription'),
        },
        {
          type: 'Kolumna',
          name: 'table.permissions',
          value: t('roles:table.permissions'),
        },
        {
          type: 'Kolumna',
          name: 'table.assignments',
          value: t('roles:table.assignments'),
        },
      ],
    },
    {
      id: getViewId('examiners'),
      name: "Widok 'Egzaminatorzy'",
      labels: [
        {
          type: 'Nagłówek widoku',
          name: 'header.title',
          value: t('examiners:header.title'),
        },
        {
          type: 'Przycisk',
          name: 'header.addExaminerButton',
          value: t('examiners:header.addExaminerButton'),
        },
        {
          type: 'Przycisk',
          name: 'header.importExaminersButton',
          value: t('examiners:header.importExaminersButton'),
        },
        {
          type: 'Kolumna',
          name: 'table.firstName',
          value: t('examiners:table.firstName'),
        },
        {
          type: 'Kolumna',
          name: 'table.lastName',
          value: t('examiners:table.lastName'),
        },
        {
          type: 'Kolumna',
          name: 'table.email',
          value: t('examiners:table.email'),
        },
        {
          type: 'Kolumna',
          name: 'table.pesel',
          value: t('examiners:table.pesel'),
        },
        {
          type: 'Kolumna',
          name: 'table.examCommittee',
          value: t('examiners:table.examCommittee'),
        },
        {
          type: 'Kolumna',
          name: 'table.phone',
          value: t('examiners:table.phone'),
        },
        {
          type: 'Kolumna',
          name: 'table.accountStatus',
          value: t('examiners:table.accountStatus'),
        },
      ],
    },
    {
      id: getViewId('cards'),
      name: "Widok 'Karty rozwiązań zadań'",
      labels: [
        {
          type: 'Nagłówek widoku',
          name: 'header.title',
          value: t('cards:header.title'),
        },
        {
          type: 'Kolumna',
          name: 'table.cardId',
          value: t('cards:table.cardId'),
        },
        {
          type: 'Kolumna',
          name: 'table.bundleNumber',
          value: t('cards:table.bundleNumber'),
        },
        {
          type: 'Kolumna',
          name: 'table.bundleName',
          value: t('cards:table.bundleName'),
        },
        {
          type: 'Kolumna',
          name: 'table.examCommitteeId',
          value: t('cards:table.examCommitteeId'),
        },
        {
          type: 'Kolumna',
          name: 'table.examineeSchoolCode',
          value: t('cards:table.examineeSchoolCode'),
        },
        {
          type: 'Kolumna',
          name: 'table.examineeIdentity',
          value: t('cards:table.examineeIdentity'),
        },
        {
          type: 'Kolumna',
          name: 'table.bundleImportDate',
          value: t('cards:table.bundleImportDate'),
        },
        {
          type: 'Kolumna',
          name: 'table.proccessDate',
          value: t('cards:table.proccessDate'),
        },
        {
          type: 'Kolumna',
          name: 'table.status',
          value: t('cards:table.status'),
        },
      ],
    },
    {
      id: getViewId('sessions'),
      name: "Widok 'Sesje egzaminacyjne'",
      labels: [
        {
          type: 'Nagłówek widoku',
          name: 'header.title',
          value: t('sessions:header.title'),
        },
        {
          type: 'Przycisk',
          name: 'header.addSessionButton',
          value: t('sessions:header.addSessionButton'),
        },
        {
          type: 'Kolumna',
          name: 'table.sessionName',
          value: t('sessions:table.sessionName'),
        },
        {
          type: 'Kolumna',
          name: 'table.examType',
          value: t('sessions:table.examType'),
        },
        {
          type: 'Kolumna',
          name: 'table.subject',
          value: t('sessions:table.subject'),
        },
        {
          type: 'Kolumna',
          name: 'table.sheetCode',
          value: t('sessions:table.sheetCode'),
        },
        {
          type: 'Kolumna',
          name: 'table.year',
          value: t('sessions:table.year'),
        },
        {
          type: 'Kolumna',
          name: 'table.month',
          value: t('sessions:table.month'),
        },
        {
          type: 'Kolumna',
          name: 'table.sourceSystemName',
          value: t('sessions:table.sourceSystemName'),
        },
      ],
    },
    {
      id: getViewId('examinees'),
      name: "Widok 'Wykaz zdających'",
      labels: [
        {
          type: 'Nagłówek widoku',
          name: 'header.title',
          value: t('examinees:header.title'),
        },
        {
          type: 'Kolumna',
          name: 'table.examCommittee',
          value: t('examinees:table.examCommittee'),
        },
        {
          type: 'Kolumna',
          name: 'table.schoolCode',
          value: t('examinees:table.schoolCode'),
        },
        {
          type: 'Kolumna',
          name: 'table.examineePesel',
          value: t('examinees:table.examineePesel'),
        },
        {
          type: 'Kolumna',
          name: 'table.examName',
          value: t('examinees:table.examName'),
        },
        {
          type: 'Kolumna',
          name: 'table.sheetCode',
          value: t('examinees:table.sheetCode'),
        },
        {
          type: 'Kolumna',
          name: 'table.room',
          value: t('examinees:table.room'),
        },
        {
          type: 'Kolumna',
          name: 'table.importDate',
          value: t('examinees:table.importDate'),
        },
        {
          type: 'Kolumna',
          name: 'table.status',
          value: t('examinees:table.status'),
        },
        {
          type: 'Kolumna',
          name: 'table.adjustments',
          value: t('examinees:table.adjustments'),
        },
      ],
    },
    {
      id: getViewId('sampleAssessment'),
      name: "Widok 'Wykaz kart zespołu wzorcowych ocen'",
      labels: [
        {
          type: 'Nagłówek widoku',
          name: 'header.title',
          value: t('sampleAssessment:header.title'),
        },
        {
          type: 'Kolumna',
          name: 'table.examiner',
          value: t('sampleAssessment:table.examiner'),
        },
        {
          type: 'Kolumna',
          name: 'table.examTaskGroupSolutionScoreId',
          value: t('sampleAssessment:table.examTaskGroupSolutionScoreId'),
        },
        {
          type: 'Kolumna',
          name: 'table.schoolId',
          value: t('sampleAssessment:table.schoolId'),
        },
        {
          type: 'Kolumna',
          name: 'table.processType',
          value: t('sampleAssessment:table.processType'),
        },
        {
          type: 'Kolumna',
          name: 'table.pointSum',
          value: t('sampleAssessment:table.pointSum'),
        },
        {
          type: 'Kolumna',
          name: 'table.progress',
          value: t('sampleAssessment:table.progress'),
        },
        {
          type: 'Kolumna',
          name: 'table.alerts',
          value: t('sampleAssessment:table.alerts'),
        },
        {
          type: 'Kolumna',
          name: 'table.acceptanceStatus',
          value: t('sampleAssessment:table.acceptanceStatus'),
        },
        {
          type: 'Kolumna',
          name: 'table.updateDate',
          value: t('sampleAssessment:table.updateDate'),
        },
      ],
    },
    {
      id: getViewId('assessments'),
      name: "Widok 'Wykaz kart zespołu egzaminatorów'",
      labels: [
        {
          type: 'Nagłówek widoku',
          name: 'header.title',
          value: t('assessments:header.title'),
        },
        {
          type: 'Kolumna',
          name: 'table.flag',
          value: t('assessments:table.flag'),
        },
        {
          type: 'Kolumna',
          name: 'table.examTaskGroupSolutionScoreId',
          value: t('assessments:table.examTaskGroupSolutionScoreId'),
        },
        {
          type: 'Kolumna',
          name: 'table.status',
          value: t('assessments:table.status'),
        },
        {
          type: 'Kolumna',
          name: 'table.pointsSum',
          value: t('assessments:table.pointsSum'),
        },
        {
          type: 'Kolumna',
          name: 'table.progress',
          value: t('assessments:table.progress'),
        },
        {
          type: 'Kolumna',
          name: 'table.alerts',
          value: t('assessments:table.alerts'),
        },
        {
          type: 'Kolumna',
          name: 'table.downloadDate',
          value: t('assessments:table.downloadDate'),
        },
        {
          type: 'Kolumna',
          name: 'table.updateDate',
          value: t('assessments:table.updateDate'),
        },
      ],
    },
    {
      id: getViewId('examinersTreemap'),
      name: "Widok 'Zarządzanie strukturą egzaminatorów'",
      labels: [
        {
          type: 'Przycisk',
          name: 'header.importTeamsButton',
          value: t('examinersTreemap:header.importTeamsButton'),
        },
        {
          type: 'Kolumna',
          name: 'table.taskGroupName',
          value: t('examinersTreemap:table.taskGroupName'),
        },
        {
          type: 'Kolumna',
          name: 'table.function',
          value: t('examinersTreemap:table.function'),
        },
        {
          type: 'Kolumna',
          name: 'table.firstName',
          value: t('examinersTreemap:table.firstName'),
        },
        {
          type: 'Kolumna',
          name: 'table.lastName',
          value: t('examinersTreemap:table.lastName'),
        },
        {
          type: 'Kolumna',
          name: 'table.examinerNumber',
          value: t('examinersTreemap:table.examinerNumber'),
        },
      ],
    },
    {
      id: getViewId('sessionsParameters'),
      name: 'Zarządzanie parametrami sesji egzaminacyjnej',
      labels: [
        {
          type: 'Nagłówek',
          name: 'tabLabels.title',
          value: t('sessionsParameters:tabLabels.title'),
        },
        {
          type: 'Zakładka boczna',
          name: 'tabLabels.sessionsParametersCardLimit',
          value: t('sessionsParameters:tabLabels.sessionsParametersCardLimit'),
        },
        {
          type: 'Zakładka boczna',
          name: 'tabLabels.sessionProcessLimits',
          value: t('sessionsParameters:tabLabels.sessionProcessLimits'),
        },
        {
          type: 'Zakładka boczna',
          name: 'tabLabels.sessionsProcessSchedule',
          value: t('sessionsParameters:tabLabels.sessionsProcessSchedule'),
        },
        {
          type: 'Zakładka boczna',
          name: 'tabLabels.controllProcess',
          value: t('sessionsParameters:tabLabels.controllProcess'),
        },
        {
          type: 'Zakładka boczna',
          name: 'tabLabels.controllToleranceRange',
          value: t('sessionsParameters:tabLabels.controllToleranceRange'),
        },
        {
          type: 'Zakładka boczna',
          name: 'tabLabels.controlTasksDistribution',
          value: t('sessionsParameters:tabLabels.controlTasksDistribution'),
        },
        {
          type: 'Zakładka boczna',
          name: 'tabLabels.examinerBlockingLimits',
          value: t('sessionsParameters:tabLabels.examinerBlockingLimits'),
        },
        {
          type: 'Zakładka boczna',
          name: 'tabLabels.examinerIndividualBlockingLimits',
          value: t('sessionsParameters:tabLabels.examinerIndividualBlockingLimits'),
        },
        {
          type: 'Zakładka boczna',
          name: 'tabLabels.qualificationProcess',
          value: t('sessionsParameters:tabLabels.qualificationProcess'),
        },
        {
          type: 'Zakładka boczna',
          name: 'tabLabels.qualificationToleranceRange',
          value: t('sessionsParameters:tabLabels.qualificationToleranceRange'),
        },
        {
          type: 'Zakładka boczna',
          name: 'tabLabels.qualificationThreshold',
          value: t('sessionsParameters:tabLabels.qualificationThreshold'),
        },
        {
          type: 'Nagłówek',
          name: 'sessionsParametersCardLimit.header',
          value: t('sessionsParameters:sessionsParametersCardLimit.header'),
        },
        {
          type: 'Kolumna',
          name: 'sessionsParametersCardLimit.table.examTaskGroupName',
          value: t('sessionsParameters:sessionsParametersCardLimit.table.examTaskGroupName'),
        },
        {
          type: 'Kolumna',
          name: 'sessionsParametersCardLimit.table.limitTotal',
          value: t('sessionsParameters:sessionsParametersCardLimit.table.limitTotal'),
        },
        {
          type: 'Kolumna',
          name: 'sessionsParametersCardLimit.table.limitDaily',
          value: t('sessionsParameters:sessionsParametersCardLimit.table.limitDaily'),
        },
        {
          type: 'Kolumna',
          name: 'sessionsParametersCardLimit.table.limitDesk',
          value: t('sessionsParameters:sessionsParametersCardLimit.table.limitDesk'),
        },
        {
          type: 'Kolumna',
          name: 'sessionsParametersCardLimit.table.extendedAccessDuration',
          value: t('sessionsParameters:sessionsParametersCardLimit.table.extendedAccessDuration'),
        },
        {
          type: 'Nagłówek',
          name: 'sessionProcessLimits.header',
          value: t('sessionsParameters:sessionProcessLimits.header'),
        },
        {
          type: 'Kolumna',
          name: 'sessionProcessLimits.table.examTaskGroupName',
          value: t('sessionsParameters:sessionProcessLimits.table.examTaskGroupName'),
        },
        {
          type: 'Kolumna',
          name: 'sessionProcessLimits.table.exercise',
          value: t('sessionsParameters:sessionProcessLimits.table.exercise'),
        },
        {
          type: 'Kolumna',
          name: 'sessionProcessLimits.table.qualification',
          value: t('sessionsParameters:sessionProcessLimits.table.qualification'),
        },
        {
          type: 'Kolumna',
          name: 'sessionProcessLimits.table.control',
          value: t('sessionsParameters:sessionProcessLimits.table.control'),
        },
        {
          type: 'Nagłówek',
          name: 'sessionsProcessSchedule.header',
          value: t('sessionsParameters:sessionsProcessSchedule.header'),
        },
        {
          type: 'Kolumna',
          name: 'sessionsProcessSchedule.table.examTaskGroupName',
          value: t('sessionsParameters:sessionsProcessSchedule.table.examTaskGroupName'),
        },
        {
          type: 'Kolumna',
          name: 'sessionsProcessSchedule.table.trainingAssessmentSessionDateFrom',
          value: t('sessionsParameters:sessionsProcessSchedule.table.trainingAssessmentSessionDateFrom'),
        },
        {
          type: 'Kolumna',
          name: 'sessionsProcessSchedule.table.qualificationAssessmentSessionDateFrom',
          value: t('sessionsParameters:sessionsProcessSchedule.table.qualificationAssessmentSessionDateFrom'),
        },
        {
          type: 'Kolumna',
          name: 'sessionsProcessSchedule.table.scoringAssessmentSessionDateFrom',
          value: t('sessionsParameters:sessionsProcessSchedule.table.scoringAssessmentSessionDateFrom'),
        },
      ],
    },
    {
      id: getViewId('systemSettings'),
      name: 'Zarządzanie ustawieniami systemowymi',
      labels: [
        {
          type: 'Zakładka boczna',
          name: 'systemModalTitles.title',
          value: t('systemSettings:systemModalTitles.title'),
        },
        {
          type: 'Zakładka boczna',
          name: 'systemModalTitles.flagSystem',
          value: t('systemSettings:systemModalTitles.flagSystem'),
        },
        {
          type: 'Zakładka boczna',
          name: 'systemModalTitles.passwordPolicyManager',
          value: t('systemSettings:systemModalTitles.passwordPolicyManager'),
        },
        {
          type: 'Zakładka boczna',
          name: 'systemModalTitles.annotationDefinitionManager',
          value: t('systemSettings:systemModalTitles.annotationDefinitionManager'),
        },
        {
          type: 'Zakładka boczna',
          name: 'systemModalTitles.configurationApi',
          value: t('systemSettings:systemModalTitles.configurationApi'),
        },
        {
          type: 'Zakładka boczna',
          name: 'systemModalTitles.specialCases',
          value: t('systemSettings:systemModalTitles.specialCases'),
        },
        {
          type: 'Zakładka boczna',
          name: 'systemModalTitles.smsNotificationSetupManager',
          value: t('systemSettings:systemModalTitles.smsNotificationSetupManager'),
        },
        {
          type: 'Zakładka boczna',
          name: 'systemModalTitles.contextualHelp',
          value: t('systemSettings:systemModalTitles.contextualHelp'),
        },
        {
          type: 'Zakładka boczna',
          name: 'systemModalTitles.title',
          value: t('systemSettings:systemModalTitles.title'),
        },
        {
          type: 'Zakładka boczna',
          name: 'systemModalTitles.labelManager',
          value: t('systemSettings:systemModalTitles.labelManager'),
        },
        {
          type: 'Zakładka boczna',
          name: 'systemModalTitles.title',
          value: t('systemSettings:systemModalTitles.title'),
        },
        {
          type: 'Zakładka boczna',
          name: 'systemModalTitles.API',
          value: t('systemSettings:systemModalTitles.API'),
        },
        {
          type: 'Nagłówek',
          name: 'passwordPolicy.header',
          value: t('systemSettings:passwordPolicy.header'),
        },
        {
          type: 'Nagłówek',
          name: 'flagSystem.header',
          value: t('systemSettings:flagSystem.header'),
        },
        {
          type: 'Nagłówek',
          name: 'flagSystem.subHeader',
          value: t('systemSettings:flagSystem.subHeader'),
        },
        {
          type: 'Nagłówek',
          name: 'smsNotificationSetupManager.header',
          value: t('systemSettings:smsNotificationSetupManager.header'),
        },
        {
          type: 'Nagłówek',
          name: 'smsNotificationSetupManager.subHeader',
          value: t('systemSettings:smsNotificationSetupManager.subHeader'),
        },
        {
          type: 'Nagłówek',
          name: 'activeTime.header',
          value: t('systemSettings:activeTime.header'),
        },
        {
          type: 'Nagłówek',
          name: 'annotationDefinitionManager',
          value: t('systemSettings:annotationDefinitionManager.header'),
        },
        {
          type: 'Przycisk',
          name: 'annotationDefinitionManager.addButton',
          value: t('systemSettings:annotationDefinitionManager.addButton'),
        },
        {
          type: 'Kolumna',
          name: 'annotationDefinitionManager.table.icon',
          value: t('systemSettings:annotationDefinitionManager.table.icon'),
        },
        {
          type: 'Kolumna',
          name: 'annotationDefinitionManager.table.annotationType',
          value: t('systemSettings:annotationDefinitionManager.table.annotationType'),
        },
        {
          type: 'Kolumna',
          name: 'annotationDefinitionManager.table.repetitive',
          value: t('systemSettings:annotationDefinitionManager.table.repetitive'),
        },
        {
          type: 'Kolumna',
          name: 'annotationDefinitionManager.table.name',
          value: t('systemSettings:annotationDefinitionManager.table.name'),
        },
        {
          type: 'Kolumna',
          name: 'annotationDefinitionManager.table.description',
          value: t('systemSettings:annotationDefinitionManager.table.description'),
        },
        {
          type: 'Kolumna',
          name: 'annotationDefinitionManager.table.active',
          value: t('systemSettings:annotationDefinitionManager.table.active'),
        },
        {
          type: 'Nagłówek',
          name: 'configurationApi.header',
          value: t('systemSettings:configurationApi.header'),
        },
        {
          type: 'Kolumna',
          name: 'configurationApi.table.name',
          value: t('systemSettings:configurationApi.table.name'),
        },
        {
          type: 'Kolumna',
          name: 'configurationApi.table.description',
          value: t('systemSettings:configurationApi.table.description'),
        },
        {
          type: 'Nagłówek',
          name: 'configurationOKE.header',
          value: t('systemSettings:configurationOKE.header'),
        },
        {
          type: 'Kolumna',
          name: 'configurationOKE.table.name',
          value: t('systemSettings:configurationOKE.table.name'),
        },
        {
          type: 'Nagłówek',
          name: 'configurationOKE.header',
          value: t('systemSettings:configurationOKE.header'),
        },
        {
          type: 'Nagłówek',
          name: 'configurationKSDO.header',
          value: t('systemSettings:configurationKSDO.header'),
        },
        {
          type: 'Nagłówek',
          name: 'specialCases.header',
          value: t('systemSettings:specialCases.header'),
        },
        {
          type: 'Kolumna',
          name: 'specialCases.table.title',
          value: t('systemSettings:specialCases.table.title'),
        },
        {
          type: 'Kolumna',
          name: 'specialCases.table.description',
          value: t('systemSettings:specialCases.table.description'),
        },
        {
          type: 'Kolumna',
          name: 'specialCases.table.recipients',
          value: t('systemSettings:specialCases.table.recipients'),
        },
        {
          type: 'Nagłówek',
          name: 'contextualHelp.header',
          value: t('systemSettings:contextualHelp.header'),
        },
        {
          type: 'Kolumna',
          name: 'contextualHelp.table.area',
          value: t('systemSettings:contextualHelp.table.area'),
        },
        {
          type: 'Kolumna',
          name: 'contextualHelp.table.section',
          value: t('systemSettings:contextualHelp.table.section'),
        },
        {
          type: 'Kolumna',
          name: 'contextualHelp.table.title',
          value: t('systemSettings:contextualHelp.table.title'),
        },
        {
          type: 'Nagłówek',
          name: 'labelManager.header',
          value: t('systemSettings:labelManager.header'),
        },
        {
          type: 'Nagłówek',
          name: 'labelManager.subHeader',
          value: t('systemSettings:labelManager.subHeader'),
        },
      ],
    },
    {
      id: getViewId('support'),
      name: 'Helpdesk',
      labels: [
        {
          type: 'Nagłówek',
          name: 'header.mainHeader',
          value: t('support:header.mainHeader'),
        },
        {
          type: 'Nagłówek',
          name: 'header.addFaq',
          value: t('support:header.addFaq'),
        },
        {
          type: 'Nagłówek',
          name: 'headeraddInstruction',
          value: t('support:header.addInstruction'),
        },
        {
          type: 'Nagłówek',
          name: 'header.instructions',
          value: t('support:header.instructions'),
        },
      ],
    },
    {
      id: getViewId('adminDashboard'),
      name: 'Panel administratora',
      labels: [
        {
          type: 'Nagłówek',
          name: 'cards:helpdesk.title',
          value: t('adminDashboard:cards:helpdesk.title'),
        },
        {
          type: 'Nagłówek',
          name: 'cards:helpdesk.description',
          value: t('adminDashboard:cards:helpdesk.description'),
        },
        {
          type: 'Nagłówek',
          name: 'cards:about.description',
          value: t('adminDashboard:cards:about.description'),
        },
      ],
    },
    {
      id: getViewId('messagebox'),
      name: 'Skrzynka odbiorcza',
      labels: [
        {
          type: 'Przycisk',
          name: 'header.addButton',
          value: t('messagebox:header.addButton'),
        },
        {
          type: 'Kolumna',
          name: 'messages.table.flag',
          value: t('messagebox:messages.table.flag'),
        },
        {
          type: 'Kolumna',
          name: 'messages.table.participant',
          value: t('messagebox:messages.table.participant'),
        },
        {
          type: 'Kolumna',
          name: 'messages.table.subject',
          value: t('messagebox:messages.table.subject'),
        },
        {
          type: 'Kolumna',
          name: 'messages.table.assessmentSession',
          value: t('messagebox:messages.table.assessmentSession'),
        },
        {
          type: 'Kolumna',
          name: 'messages.table.monit',
          value: t('messagebox:messages.table.monit'),
        },
        {
          type: 'Kolumna',
          name: 'messages.table.sendDate',
          value: t('messagebox:messages.table.sendDate'),
        },
      ],
    },
    {
      id: getViewId('calendar'),
      name: 'Kalendarz zdarzeń',
      labels: [
        {
          type: 'Nagłówek',
          name: 'header.title',
          value: t('calendar:header.title'),
        },
        {
          type: 'Nagłówek',
          name: 'header.currentMonthButton',
          value: t('calendar:header.currentMonthButton'),
        },
      ],
    },
    {
      id: getViewId('context'),
      name: 'Wybór kontekstu pracy',
      labels: [
        {
          type: 'Przycisk',
          name: 'buttons.admin',
          value: t('context:buttons.admin'),
        },
        {
          type: 'Przycisk',
          name: 'buttons.examiner',
          value: t('context:buttons.examiner'),
        },
      ],
    },
    {
      id: getViewId('global'),
      name: 'Ekran logowania, nazwy skrócone i menu boczne',
      labels: [
                {
          type: 'Pełna nazwa systemu',
          name: 'sidebar.systemFullName',
          value: t('global:sidebar.systemFullName'),
        },
        {
          type: 'Nazwa skrócona',
          name: 'shortNames.system',
          value: t('global:shortNames.system'),
        },
        {
          type: 'Nazwa skrócona',
          name: 'shortNames.beneficiary',
          value: t('global:shortNames.beneficiary'),
        },
        {
          type: 'Nazwa skrócona',
          name: 'shortNames.OKE',
          value: t('global:shortNames.OKE'),
        },
        {
          type: 'Nazwa skrócona',
          name: 'shortNames.KSDO',
          value: t('global:shortNames.KSDO'),
        },
        {
          type: 'Nazwa skrócona',
          name: 'shortNames.SIOEO',
          value: t('global:shortNames.SIOEO'),
        },
        {
          type: 'Menu boczne',
          name: 'sidebar.users',
          value: t('global:sidebar.users'),
        },
        {
          type: 'Menu boczne',
          name: 'sidebar.roles',
          value: t('global:sidebar.roles'),
        },
        {
          type: 'Menu boczne',
          name: 'sidebar.examiners',
          value: t('global:sidebar.examiners'),
        },
        {
          type: 'Menu boczne',
          name: 'sidebar.cards',
          value: t('global:sidebar.cards'),
        },
        {
          type: 'Menu boczne',
          name: 'sidebar.sessions',
          value: t('global:sidebar.sessions'),
        },
        {
          type: 'Menu boczne',
          name: 'sidebar.sampleAssessmentRepository',
          value: t('global:sidebar.sampleAssessmentRepository'),
        },
        {
          type: 'Menu boczne',
          name: 'sidebar.examinees',
          value: t('global:sidebar.examinees'),
        },
      ],
    },
  ];

  const columns: SelectableTableColumn[] = [
    {
      id: 'type',
      type: 'text',
      title: 'Rodzaj',
      filterType: null,
    },
    {
      id: 'name',
      type: 'text',
      title: 'Nazwa etykiety',
      filterType: null,
    },
    {
      id: 'actions',
      type: 'actions',
      title: 'Akcje',
      filterType: null,
      skipEnterMethod: true,
      width: '10%',
    },
  ];

  const handleEditLabel = (label: LabelsListItem) => {
    setSelectedLabel(label);
    setIsEditLabelModalOpen(true);
  };

  const getActionsMenuIcon = (label: LabelsListItem) => {
    return (
      <Link
        style={{ cursor: 'pointer' }}
        onClick={() => {
          handleEditLabel(label);
        }}
      >
        Edytuj
      </Link>
    );
  };

  useEffect(() => {
    if (!selectedView) return;
    const data = selectedView.labels.map((label) => ({
      type: label.type,
      name: label.value,
      actions: getActionsMenuIcon(label),
    }));
    setTableDataMap((prev) => ({ ...prev, [selectedView.id]: data }));
  }, [selectedView]);

  const handleOnClose = () => {
    setIsEditLabelModalOpen(false);
  };

  const refreshLabel = (name: string, value: string) => {
    if (selectedView) {
      const updatedView = Object.assign(selectedView);
      updatedView.labels.map((label: LabelsListItem) => {
        if (label.name === name) {
          label.value = value;
        }
      });
      setSelectedView((prevState) => {
        if (!prevState) return;
        return { ...prevState, updatedView };
      });
    }
  };

  const handleSave = (name: string, value: string) => {
    if (!selectedView) return;

    const currentView = allLabels.filter((e) => e.id === selectedView.id);
    const updatedView = Object.assign(currentView);
    const updatedViewContent = JSON.parse(updatedView[0]?.content);

    const labelNames = name.split('.');
    if (labelNames.length === 1) {
      updatedViewContent[labelNames[0]] = value;
    }
    if (labelNames.length === 2) {
      updatedViewContent[labelNames[0]][labelNames[1]] = value;
    }
    if (labelNames.length === 3) {
      updatedViewContent[labelNames[0]][labelNames[1]][labelNames[2]] = value;
    }

    showLoader(true);

    updateLabel(selectedView.id, updatedViewContent)
      .then(
        () => {
          openAlert('success');
          refreshLabel(name, value);
        },
        () => {
          openAlert('save_error');
        },
      )
      .finally(() => {
        showLoader(false);
        setIsEditLabelModalOpen(false);
        getData();
        i18next.reloadResources();
      });
  };

  const handleChange = (viewId: string) => {
    if (expandedViews.indexOf(viewId) === -1) {
      setExpandedViews((arr) => [...arr, viewId]);
    } else {
      setExpandedViews(expandedViews.filter((el: string) => el !== viewId));
    }
  };

  const isExpandedView = (viewId: string) => {
    return expandedViews.find((view) => view === viewId);
  };

  return (
    <>
      <Grid item xs={12}>
        <div className={classes.listHeader}>
          <div style={{ height: '100px' }}>
            <Typography className={classes.tabTitle}>{t('systemSettings:labelManager.header')}</Typography>
            <Typography className={classes.tabSubTitle}>Wybierz widok, dla którego chcesz edytować etykiety</Typography>
          </div>
        </div>
      </Grid>
      <Grid item xs={12}>
        <div style={{ marginTop: '25px', maxHeight: '400px', overflow: 'scroll' }}>
          {viewsWithLabels.map((view) => {
            return (
              <div key={`${view.id}_${view.name}`}>
                <Accordion
                  key={view.id}
                  square
                  disableGutters
                  elevation={0}
                  sx={{
                    '&:before': {
                      display: 'none',
                    },
                    margin: '0px',
                    background: 'none',
                    boxShadow: 'none',
                    borderBottom: '1px solid #2c2d3e',
                  }}
                  TransitionProps={{
                    timeout: 0,
                    unmountOnExit: true,
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <TooltipOneComponent
                        placement="top"
                        title={
                          isExpandedView(view.id)
                            ? `Zwiń listę etykiet dla widoku: ${view.name}`
                            : `Rozwiń listę etykiet dla widoku: ${view.name}`
                        }
                      >
                        {isExpandedView(view.id) ? <CollapseIcon /> : <ExpandIcon />}
                      </TooltipOneComponent>
                    }
                    aria-controls="panel1-content"
                    id={view.id}
                    sx={{
                      margin: '0px',
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        transition: 'none',
                        '&.Mui-expanded': {
                          transform: 'none',
                        },
                      },
                    }}
                    onClick={() => {
                      handleChange(view.id);
                      setSelectedView(view);
                    }}
                  >
                    <Typography style={{ fontWeight: 'bold' }}>{view.name}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>
                      <SelectableTable<LabelsListTableItem>
                        columns={columns}
                        rows={tableDataMap[view.id] || []}
                        caption={t('systemSettings:labelManager.header')}
                      />
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </div>
            );
          })}
        </div>
      </Grid>
      {isEditLabelModalOpen && (
        <EditLabel
          label={selectedLabel}
          onCancel={() => setIsEditLabelModalOpen(false)}
          onChange={handleSave}
          onClose={handleOnClose}
        />
      )}
    </>
  );
};
